import { useRef, useEffect, useState } from "react";
import { BarChart3, Users, Wallet, MessageCircle, DollarSign, Bell, Settings, UserCheck, CreditCard, Coins, Bot, Radio, ChevronLeft, ChevronRight } from "lucide-react";

const AdminTabs = ({ activeTab, setActiveTab, tradesCount, usersCount, pendingWithdrawals, unreadTotal, pendingApprovals }) => {
  const tabs = [
    { id: "approvals", label: "Регистрации", icon: UserCheck, badge: pendingApprovals },
    { id: "trades", label: `Сделки (${tradesCount})`, icon: BarChart3 },
    { id: "users", label: `Пользователи (${usersCount})`, icon: Users },
    { id: "withdrawals", label: `Выводы (${pendingWithdrawals})`, icon: Wallet },
    { id: "chat", label: "Чат", icon: MessageCircle, badge: unreadTotal },
    { id: "deposits", label: "Пополнения", icon: DollarSign },
    { id: "wallets", label: "Кошельки", icon: CreditCard },
    { id: "assets", label: "Активы", icon: Coins },
    { id: "autotrading", label: "Авто-трейдинг", icon: Bot },
    { id: "autodeposits", label: "Авто-депозиты", icon: Radio },
    { id: "notifications", label: "Нотификации", icon: Bell },
    { id: "settings", label: "Настройки", icon: Settings },
  ];

  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 5);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  // Re-evaluate on tabs change (badges may resize the container)
  useEffect(() => {
    updateArrows();
  }, [tradesCount, usersCount, pendingWithdrawals, unreadTotal, pendingApprovals]);

  // Scroll active tab into view smoothly
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const active = el.querySelector(`[data-testid="${activeTab}-tab"]`);
    if (active) active.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [activeTab]);

  const scrollBy = (delta) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <div className="relative mb-8 border-b border-[#1F1F1F]">
      {/* Left arrow */}
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-260)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 -mt-2 flex items-center justify-center bg-[#000000] border border-[#1F1F1F] hover:border-[#2E2E2E] hover:text-[#EDEDED] text-white rounded-full shadow-lg transition-colors"
          data-testid="tabs-scroll-left"
          aria-label="Назад"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* Scrollable tabs */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth px-6"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-2 font-medium flex items-center gap-2 whitespace-nowrap relative ${
                activeTab === tab.id
                  ? "text-[#FF8A1E] border-b-2 border-[#FF8A1E]"
                  : "text-[#A1A1AA] hover:text-white"
              }`}
              data-testid={`${tab.id}-tab`}
            >
              <Icon size={18} />
              {tab.label}
              {tab.badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full text-white text-xs flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Right arrow */}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollBy(260)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 -mt-2 flex items-center justify-center bg-[#000000] border border-[#1F1F1F] hover:border-[#2E2E2E] hover:text-[#EDEDED] text-white rounded-full shadow-lg transition-colors"
          data-testid="tabs-scroll-right"
          aria-label="Вперёд"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
};

export default AdminTabs;
